import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Icon, Input, Select, Spinner, Textarea, useToast } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  disablePush, enablePush, getPushStatus, isIos, isStandalone, pushSupported, testPush,
  PushError, type PushStatus
} from '../../lib/push';
import { usePageMeta } from '../../lib/seo';
import { applyTheme, BUSINESS_THEMES, type ThemeOption } from '../../lib/theme';
import { tr } from '../../lib/i18n';
import { LanguageCard } from '../../components/LanguageSelect';

interface BusinessRow {
  id: string;
  name: string;
  description: string | null;
  headline: string | null;
  category_key: string | null;
  address_street: string | null;
  address_number: string | null;
  address_district: string | null;
  address_city: string | null;
  address_state: string | null;
  address_cep: string | null;
  phone: string | null;
  whatsapp_phone: string | null;
  timezone: string;
  policies: string | null;
  theme: string | null;
  logo_path: string | null;
  cover_path: string | null;
  instagram: string | null;
  facebook: string | null;
  tiktok: string | null;
  website: string | null;
}

interface Category { key: string; label: string }
interface Fail { status: number; message: string }

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function toFail(err: unknown): Fail {
  if (err instanceof ApiError) return { status: err.status, message: err.message };
  return { status: 0, message: err instanceof Error ? err.message : tr('Não foi possível concluir a operação.') };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(tr('Falha ao ler o arquivo.')));
    reader.readAsDataURL(file);
  });
}

function ThemeChoice({
  option, selected, disabled, onSelect
}: {
  option: ThemeOption | null;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}): JSX.Element {
  const name = option ? tr(option.name) : tr('Padrão da plataforma');
  const description = option ? tr(option.description) : tr('Acompanha o tema definido pela administração.');
  const accent = option?.swatch.accent ?? 'var(--nl-graphite)';
  const gradient = option?.gradient ?? 'linear-gradient(135deg, #2b3d4d 0%, #101a24 58%, #0a1119 100%)';
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      style={{ borderColor: selected ? accent : undefined }}
      className={`relative overflow-hidden rounded-2xl border-2 bg-card text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
        selected ? 'shadow-[0_16px_30px_-18px_rgba(16,26,36,0.7)]' : 'border-transparent ring-1 ring-line hover:ring-[var(--nl-border-strong)]'
      }`}
    >
      <span className="relative block h-16 w-full" style={{ background: gradient }} aria-hidden="true">
        <span className="absolute bottom-2.5 left-2.5 h-5 w-12 rounded-full bg-white/85" />
        <span className="absolute bottom-2.5 left-[4.1rem] h-5 w-5 rounded-full bg-white/40" />
        {selected ? (
          <span
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm"
            style={{ color: accent }}
          >
            <Icon name="check" size={14} />
          </span>
        ) : null}
      </span>
      <span className="block px-3 pb-3 pt-2">
        <span className="block text-sm font-semibold">{name}</span>
        <span className="mt-0.5 block text-xs text-muted">{description}</span>
      </span>
    </button>
  );
}

function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 || digits.length === 10) return `55${digits}`;
  if (digits.length === 12 && digits.startsWith('55')) return digits;
  if (digits.length >= 8) return digits;
  return null;
}

export function PanelSettingsPage(): JSX.Element {
  usePageMeta(tr('Configurações'), tr('Dados do negócio, aparência da loja, conta, senha e exclusão.'), '/painel/configuracoes');
  const toast = useToast();
  const navigate = useNavigate();
  const { active, user, logout, refresh } = useAuth();
  const bid = active?.businessId ?? '';
  const role = active?.role ?? 'professional';
  const perms = active?.permissions ?? [];
  const canEditBusiness = role === 'owner' || (role === 'manager' && (perms.includes('*') || perms.includes('business_settings')));
  const isOwner = role === 'owner';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Fail | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [bizError, setBizError] = useState<Fail | null>(null);
  const [savingBiz, setSavingBiz] = useState(false);

  const [form, setForm] = useState<Record<string, string>>({});
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [coverPath, setCoverPath] = useState<string | null>(null);
  const [theme, setTheme] = useState('');
  const [savedTheme, setSavedTheme] = useState('');
  const savedThemeRef = useRef<string>(document.documentElement.getAttribute('data-theme') ?? '');
  const [appearanceError, setAppearanceError] = useState<Fail | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const logoInput = useRef<HTMLInputElement>(null);
  const coverInput = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [userPhone, setUserPhone] = useState(user?.phone ?? '');
  const [savingUser, setSavingUser] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deletePw, setDeletePw] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState<string | null>(null);

  const pushAvailable = pushSupported();
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [pushLoading, setPushLoading] = useState(true);
  const [pushBusy, setPushBusy] = useState<'enable' | 'disable' | 'test' | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  const set = (k: string): (v: string) => void => (v) => setForm((prev) => ({ ...prev, [k]: v }));

  const load = useCallback(async (): Promise<void> => {
    if (!bid) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const b = await api.get<{ business: BusinessRow }>(`/api/businesses/${bid}`);
      const row = b.business;
      setForm({
        name: row.name,
        description: row.description ?? '',
        headline: row.headline ?? '',
        categoryKey: row.category_key ?? '',
        addressStreet: row.address_street ?? '',
        addressNumber: row.address_number ?? '',
        addressDistrict: row.address_district ?? '',
        addressCity: row.address_city ?? '',
        addressState: row.address_state ?? '',
        addressCep: row.address_cep ?? '',
        phone: row.phone ?? '',
        whatsappPhone: row.whatsapp_phone ?? '',
        timezone: row.timezone,
        policies: row.policies ?? '',
        instagram: row.instagram ?? '',
        facebook: row.facebook ?? '',
        tiktok: row.tiktok ?? '',
        website: row.website ?? ''
      });
      setLogoPath(row.logo_path ?? null);
      setCoverPath(row.cover_path ?? null);
      setTheme(row.theme ?? '');
      setSavedTheme(row.theme ?? '');
      const s = await api.get<{ categories: Category[] }>(`/api/services/${bid}/services`);
      setCategories(s.categories);
    } catch (err) {
      setLoadError(toFail(err));
    } finally {
      setLoading(false);
    }
  }, [bid]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading || !bid || !pushAvailable) {
      setPushLoading(false);
      return;
    }
    let cancelled = false;
    setPushLoading(true);
    void getPushStatus(bid)
      .then((s) => { if (!cancelled) setPushStatus(s); })
      .catch(() => { if (!cancelled) setPushStatus(null); })
      .finally(() => { if (!cancelled) setPushLoading(false); });
    return () => { cancelled = true; };
  }, [bid, loading, pushAvailable]);

  useEffect(() => {
    if (loading || !bid) return;
    applyTheme(theme === '' ? savedThemeRef.current : theme, false);
  }, [theme, loading, bid]);

  useEffect(() => () => {
    applyTheme(savedThemeRef.current, false);
  }, []);

  const phonePreview = normalizePhone(form.phone ?? '');
  const waPreview = normalizePhone(form.whatsappPhone ?? '');
  const phoneInvalid = (form.phone ?? '').trim().length > 0 && (phonePreview === null || phonePreview.length < 10);
  const waInvalid = (form.whatsappPhone ?? '').trim().length > 0 && (waPreview === null || waPreview.length < 12);

  async function saveBusiness(): Promise<void> {
    setSavingBiz(true);
    setBizError(null);
    try {
      const body: Record<string, string> = {
        description: form.description ?? '',
        headline: form.headline ?? '',
        categoryKey: form.categoryKey ?? '',
        addressStreet: form.addressStreet ?? '',
        addressNumber: form.addressNumber ?? '',
        addressDistrict: form.addressDistrict ?? '',
        addressCity: form.addressCity ?? '',
        addressState: (form.addressState ?? '').toUpperCase(),
        addressCep: form.addressCep ?? '',
        phone: form.phone ?? '',
        whatsappPhone: form.whatsappPhone ?? '',
        timezone: form.timezone ?? '',
        policies: form.policies ?? '',
        instagram: form.instagram ?? '',
        facebook: form.facebook ?? '',
        tiktok: form.tiktok ?? '',
        website: form.website ?? ''
      };
      if (isOwner && form.name) body.name = form.name;
      await api.patch(`/api/businesses/${bid}`, body);
      toast(tr('Dados do negócio salvos.'), 'success');
      await load();
      await refresh();
    } catch (err) {
      setBizError(toFail(err));
    } finally {
      setSavingBiz(false);
    }
  }

  async function patchAppearance(body: { logoPath?: string; coverPath?: string; theme?: string }): Promise<boolean> {
    setAppearanceError(null);
    try {
      await api.patch(`/api/businesses/${bid}`, body);
      await refresh();
      return true;
    } catch (err) {
      setAppearanceError(toFail(err));
      return false;
    }
  }

  async function onLogoPick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setAppearanceError({ status: 0, message: 'Formato não suportado. Use JPG, PNG, WEBP ou GIF.' });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setAppearanceError({ status: 0, message: 'A imagem excede 5 MB. Escolha um arquivo menor.' });
      return;
    }
    setUploadingLogo(true);
    setAppearanceError(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const uploaded = await api.post<{ path: string }>(`/api/businesses/${bid}/upload`, { filename: file.name, data: dataUrl });
      setLogoPath(uploaded.path);
      if (await patchAppearance({ logoPath: uploaded.path })) toast('Logo atualizada.', 'success');
      else await load();
    } catch (err) {
      setAppearanceError(toFail(err));
    } finally {
      setUploadingLogo(false);
    }
  }

  async function removeLogo(): Promise<void> {
    setUploadingLogo(true);
    setAppearanceError(null);
    try {
      if (await patchAppearance({ logoPath: '' })) {
        setLogoPath(null);
        toast('Logo removida.', 'success');
      } else {
        await load();
      }
    } finally {
      setUploadingLogo(false);
    }
  }

  async function onCoverPick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setAppearanceError({ status: 0, message: 'Formato não suportado. Use JPG, PNG, WEBP ou GIF.' });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setAppearanceError({ status: 0, message: 'A imagem excede 5 MB. Escolha um arquivo menor.' });
      return;
    }
    setUploadingCover(true);
    setAppearanceError(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const uploaded = await api.post<{ path: string }>(`/api/businesses/${bid}/upload`, { filename: file.name, data: dataUrl });
      setCoverPath(uploaded.path);
      if (await patchAppearance({ coverPath: uploaded.path })) toast(tr('Capa atualizada.'), 'success');
      else await load();
    } catch (err) {
      setAppearanceError(toFail(err));
    } finally {
      setUploadingCover(false);
    }
  }

  async function removeCover(): Promise<void> {
    setUploadingCover(true);
    setAppearanceError(null);
    try {
      if (await patchAppearance({ coverPath: '' })) {
        setCoverPath(null);
        toast(tr('Capa removida.'), 'success');
      } else {
        await load();
      }
    } finally {
      setUploadingCover(false);
    }
  }
  async function saveTheme(): Promise<void> {
    setSavingTheme(true);
    setAppearanceError(null);
    try {
      if (await patchAppearance({ theme })) {
        savedThemeRef.current = theme;
        setSavedTheme(theme);
        toast(theme ? tr('Tema da loja atualizado.') : tr('A loja voltou a usar o tema padrão da plataforma.'), 'success');
      }
    } finally {
      setSavingTheme(false);
    }
  }

  async function saveUser(): Promise<void> {
    setSavingUser(true);
    try {
      await api.patch('/api/auth/me', { fullName: fullName.trim(), phone: userPhone.trim() });
      toast(tr('Dados da conta atualizados.'), 'success');
      await refresh();
    } catch (err) {
      toast(toFail(err).message, 'danger');
    } finally {
      setSavingUser(false);
    }
  }

  async function changePassword(): Promise<void> {
    setSavingPw(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword: curPw, newPassword: newPw });
      toast(tr('Senha alterada. Outras sessões foram encerradas.'), 'success');
      setCurPw('');
      setNewPw('');
    } catch (err) {
      toast(toFail(err).message, 'danger');
    } finally {
      setSavingPw(false);
    }
  }

  function pushMessage(err: unknown): string {
    if (err instanceof PushError || err instanceof ApiError) return err.message;
    return tr('Não foi possível concluir a operação de notificações.');
  }

  async function onEnablePush(): Promise<void> {
    setPushBusy('enable');
    setPushError(null);
    try {
      setPushStatus(await enablePush(bid));
      toast('Notificações ativadas neste aparelho.', 'success');
    } catch (err) {
      const message = pushMessage(err);
      setPushError(message);
      toast(message, 'danger');
    } finally {
      setPushBusy(null);
    }
  }

  async function onDisablePush(): Promise<void> {
    setPushBusy('disable');
    setPushError(null);
    try {
      setPushStatus(await disablePush(bid));
      toast(tr('Notificações desativadas neste aparelho.'), 'success');
    } catch (err) {
      const message = pushMessage(err);
      setPushError(message);
      toast(message, 'danger');
    } finally {
      setPushBusy(null);
    }
  }

  async function onTestPush(): Promise<void> {
    setPushBusy('test');
    setPushError(null);
    try {
      const res = await testPush(bid);
      toast(
        res.failed > 0
          ? tr('Teste aceito em {sent} aparelho(s); {failed} falhou(aram).', { sent: res.sent, failed: res.failed })
          : tr('Teste enviado para {sent} aparelho(s).', { sent: res.sent }),
        res.failed > 0 ? 'info' : 'success'
      );
    } catch (err) {
      const message = pushMessage(err);
      setPushError(message);
      toast(message, 'danger');
    } finally {
      setPushBusy(null);
    }
  }

  async function deleteAccount(): Promise<void> {
    setDeleting(true);
    setDeleteInfo(null);
    try {
      const res = await api.post<{ message: string }>('/api/auth/delete-account', { password: deletePw });
      setDeleteInfo(res.message);
      await logout();
      toast('Conta desativada.', 'success');
      window.setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      setDeleteInfo(toFail(err).message);
      toast(toFail(err).message, 'danger');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Configurações")}</h1>
        <p className="text-sm text-muted">{tr("Dados do estabelecimento, sua conta e segurança.")}</p>
      </div>

      <LanguageCard />

      {loadError ? <ErrorAlert error={loadError} /> : null}

      {loading ? (
        <Card><Spinner label={tr("Carregando configurações…")} /></Card>
      ) : !bid ? (
        <Card as="section"><p className="text-sm">{tr("Nenhum estabelecimento selecionado.")}</p></Card>
      ) : (
        <Card as="section">
          <h2 className="mb-1 text-lg font-semibold">{tr("Dados do negócio")}</h2>
          <p className="mb-3 text-sm text-muted">{canEditBusiness ? tr('Estas informações aparecem no seu perfil público e na busca.') : tr('Somente leitura para o seu papel/permissão.')}</p>
          {bizError ? <div className="mb-3"><ErrorAlert error={bizError} /></div> : null}
          {!canEditBusiness ? <div className="mb-3"><Alert tone="info" title={tr("Sem permissão")}>{tr("Seu papel não permite editar os dados do estabelecimento.")}</Alert></div> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="nl-label">{tr("Nome")} {isOwner ? '' : '(só o proprietário edita)'}</span>
              <Input value={form.name ?? ''} onChange={(e) => set('name')(e.target.value)} disabled={!canEditBusiness || !isOwner} maxLength={120} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Categoria")}</span>
              <Select value={form.categoryKey ?? ''} onChange={(e) => set('categoryKey')(e.target.value)} disabled={!canEditBusiness}>
                <option value="">{tr("— Selecione —")}</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{tr(c.label)}</option>)}
              </Select>
            </label>
            <label className="block sm:col-span-2">
              <span className="nl-label">{tr("Frase do perfil")}</span>
              <Input value={form.headline ?? ''} onChange={(e) => set('headline')(e.target.value)} disabled={!canEditBusiness} maxLength={160} placeholder={tr("Ex.: Cortes clássicos com atendimento personalizado no centro da cidade.")} />
              <span className="nl-hint mt-1 block">{tr("Aparece em destaque abaixo do seu nome no perfil público.")}</span>
            </label>
            <label className="block sm:col-span-2">
              <span className="nl-label">{tr("Descrição")}</span>
              <Textarea value={form.description ?? ''} onChange={(e) => set('description')(e.target.value)} rows={3} disabled={!canEditBusiness} maxLength={2000} />
            </label>
            <label className="block sm:col-span-2">
              <span className="nl-label">{tr("Endereço (rua)")}</span>
              <Input value={form.addressStreet ?? ''} onChange={(e) => set('addressStreet')(e.target.value)} disabled={!canEditBusiness} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Número")}</span>
              <Input value={form.addressNumber ?? ''} onChange={(e) => set('addressNumber')(e.target.value)} disabled={!canEditBusiness} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Bairro")}</span>
              <Input value={form.addressDistrict ?? ''} onChange={(e) => set('addressDistrict')(e.target.value)} disabled={!canEditBusiness} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Cidade")}</span>
              <Input value={form.addressCity ?? ''} onChange={(e) => set('addressCity')(e.target.value)} disabled={!canEditBusiness} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Estado (UF)")}</span>
              <Input value={form.addressState ?? ''} onChange={(e) => set('addressState')(e.target.value)} disabled={!canEditBusiness} maxLength={2} placeholder={tr("SP")} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("CEP")}</span>
              <Input value={form.addressCep ?? ''} onChange={(e) => set('addressCep')(e.target.value)} disabled={!canEditBusiness} maxLength={9} placeholder="00000-000" />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Fuso horário")}</span>
              <Input value={form.timezone ?? ''} onChange={(e) => set('timezone')(e.target.value)} disabled={!canEditBusiness} placeholder={tr("America/Sao_Paulo")} />
            </label>
            <label className="block">
              <span className="nl-label">{tr("Telefone comercial")}</span>
              <Input value={form.phone ?? ''} onChange={(e) => set('phone')(e.target.value)} disabled={!canEditBusiness} invalid={phoneInvalid} placeholder="551133334444" />
              <span className={`nl-hint mt-1 block ${phoneInvalid ? 'text-danger' : ''}`}>
                {phoneInvalid ? tr('Número inválido. Use o formato internacional: 55 + DDD + número.') : phonePreview ? tr('Salvo como: {phone}', { phone: phonePreview }) : tr('Formato internacional: 55 + DDD + número.')}
              </span>
            </label>
            <label className="block">
              <span className="nl-label">{tr("WhatsApp")}</span>
              <Input value={form.whatsappPhone ?? ''} onChange={(e) => set('whatsappPhone')(e.target.value)} disabled={!canEditBusiness} invalid={waInvalid} placeholder="5511999998888" />
              <span className={`nl-hint mt-1 block ${waInvalid ? 'text-danger' : ''}`}>
                {waInvalid ? tr('WhatsApp precisa de 55 + DDD + 9 dígitos (celular).') : waPreview ? tr('Salvo como: {phone}', { phone: waPreview }) : tr('Usado para o botão de WhatsApp no perfil.')}
              </span>
            </label>
            <div className="border-t border-line pt-3 sm:col-span-2">
              <h3 className="text-sm font-semibold">{tr("Redes sociais")}</h3>
              <p className="nl-hint mt-0.5">{tr("Preencha apenas as que você usa. Os links aparecem no perfil público.")}</p>
            </div>
            <label className="block">
              <span className="nl-label">{tr("Instagram")}</span>
              <Input value={form.instagram ?? ''} onChange={(e) => set('instagram')(e.target.value)} disabled={!canEditBusiness} placeholder="@seu_instagram" maxLength={200} />
              <span className="nl-hint mt-1 block">{tr("Usuário ou link completo do perfil.")}</span>
            </label>
            <label className="block">
              <span className="nl-label">{tr("Facebook")}</span>
              <Input value={form.facebook ?? ''} onChange={(e) => set('facebook')(e.target.value)} disabled={!canEditBusiness} placeholder="@sua_pagina" maxLength={200} />
              <span className="nl-hint mt-1 block">{tr("Usuário ou link completo da página.")}</span>
            </label>
            <label className="block">
              <span className="nl-label">{tr("TikTok")}</span>
              <Input value={form.tiktok ?? ''} onChange={(e) => set('tiktok')(e.target.value)} disabled={!canEditBusiness} placeholder="@seu_tiktok" maxLength={200} />
              <span className="nl-hint mt-1 block">{tr("Usuário ou link completo do perfil.")}</span>
            </label>
            <label className="block">
              <span className="nl-label">{tr("Site")}</span>
              <Input value={form.website ?? ''} onChange={(e) => set('website')(e.target.value)} disabled={!canEditBusiness} placeholder="https://seusite.com" maxLength={300} />
              <span className="nl-hint mt-1 block">{tr("Endereço do seu site, se tiver.")}</span>
            </label>
            <label className="block sm:col-span-2">
              <span className="nl-label">{tr("Políticas (cancelamento, atrasos, etc.)")}</span>
              <Textarea value={form.policies ?? ''} onChange={(e) => set('policies')(e.target.value)} rows={3} disabled={!canEditBusiness} maxLength={2000} />
            </label>
          </div>
          {canEditBusiness ? <div className="mt-4"><Button onClick={() => void saveBusiness()} loading={savingBiz}>{tr("Salvar dados do negócio")}</Button></div> : null}
        </Card>
      )}

      {!loading && bid ? (
        <Card as="section">
          <h2 className="mb-1 text-lg font-semibold">{tr("Aparência da loja")}</h2>
          <p className="mb-4 text-sm text-muted">{tr("Sua logo substitui a marca AgendaPro no painel e no perfil público. O tema escolhe as cores do perfil.")}</p>
          {appearanceError ? <div className="mb-3"><ErrorAlert error={appearanceError} /></div> : null}
          {!canEditBusiness ? <div className="mb-3"><Alert tone="info" title={tr("Sem permissão")}>{tr("Seu papel não permite alterar a aparência da loja.")}</Alert></div> : null}
          <div className="mb-6">
            <span className="nl-label">{tr("Capa do perfil")}</span>
            <div className="mt-2 h-32 overflow-hidden rounded-2xl border border-line bg-surface sm:h-40">
              {coverPath ? (
                <img src={coverPath} alt={tr("Capa do perfil")} className="h-full w-full object-cover" />
              ) : (
                <div
                  className="flex h-full w-full items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, var(--nl-accent) 0%, var(--nl-graphite) 100%)' }}
                >
                  <span className="rounded-full bg-black/25 px-3 py-1 text-xs font-semibold text-white">{tr('Usando o gradiente do tema')}</span>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button className="px-3 py-2" variant="secondary" loading={uploadingCover} disabled={!canEditBusiness} onClick={() => coverInput.current?.click()}>
                {coverPath ? tr('Trocar capa') : tr('Enviar capa')}
              </Button>
              {coverPath ? (
                <Button className="px-3 py-2" variant="ghost" disabled={!canEditBusiness || uploadingCover} onClick={() => void removeCover()}>
                  {tr("Remover")}
                </Button>
              ) : null}
            </div>
            <input
              ref={coverInput}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              className="hidden"
              onChange={(e) => void onCoverPick(e)}
            />
            <span className="nl-hint mt-2 block">{tr("Imagem larga, como 1600 × 600 px. JPG, PNG, WEBP ou GIF até 5 MB.")}</span>
          </div>
          <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
            <div>
              <span className="nl-label">{tr("Logo da loja")}</span>
              <div className="mt-2 flex h-24 items-center justify-center rounded-2xl border border-line bg-surface p-3">
                {logoPath ? (
                  <img src={logoPath} alt={tr("Logo da loja")} className="max-h-20 max-w-full object-contain" />
                ) : (
                  <span className="text-sm text-muted">{tr("Sem logo")}</span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button className="px-3 py-2" variant="secondary" loading={uploadingLogo} disabled={!canEditBusiness} onClick={() => logoInput.current?.click()}>
                  {logoPath ? tr('Trocar logo') : tr('Enviar logo')}
                </Button>
                {logoPath ? (
                  <Button className="px-3 py-2" variant="ghost" disabled={!canEditBusiness || uploadingLogo} onClick={() => void removeLogo()}>
                    {tr("Remover")}
                  </Button>
                ) : null}
              </div>
              <input
                ref={logoInput}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                className="hidden"
                onChange={(e) => void onLogoPick(e)}
              />
              <span className="nl-hint mt-2 block">{tr("JPG, PNG, WEBP ou GIF até 5 MB. PNG com fundo transparente tem melhor resultado.")}</span>
            </div>

            <div>
              <span className="nl-label">{tr("Tema do perfil")}</span>
              <div className="mt-2 space-y-3">
                <div>
                  <span className="nl-hint block font-semibold">{tr("Feminino")}</span>
                  <div className="mt-1 grid gap-2 sm:grid-cols-3">
                    {BUSINESS_THEMES.filter((option) => option.group === 'Feminino').map((option) => (
                      <ThemeChoice
                        key={option.id}
                        option={option}
                        selected={theme === option.id}
                        disabled={!canEditBusiness}
                        onSelect={() => setTheme(option.id)}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <span className="nl-hint block font-semibold">{tr("Masculino")}</span>
                  <div className="mt-1 grid gap-2 sm:grid-cols-3">
                    {BUSINESS_THEMES.filter((option) => option.group === 'Masculino').map((option) => (
                      <ThemeChoice
                        key={option.id}
                        option={option}
                        selected={theme === option.id}
                        disabled={!canEditBusiness}
                        onSelect={() => setTheme(option.id)}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <span className="nl-hint block font-semibold">{tr("Padrão")}</span>
                  <div className="mt-1 grid gap-2 sm:grid-cols-3">
                    <ThemeChoice option={null} selected={theme === ''} disabled={!canEditBusiness} onSelect={() => setTheme('')} />
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <Button className="px-3 py-2" onClick={() => void saveTheme()} loading={savingTheme} disabled={!canEditBusiness || theme === savedTheme}>
                  {tr("Salvar tema")}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {!loading && bid ? (
        <Card as="section">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="mb-1 text-lg font-semibold">{tr("Notificações")}</h2>
              <p className="text-sm text-muted">{tr("Receba um aviso no computador e no celular quando um cliente marcar um horário.")}</p>
            </div>
            {pushAvailable && !pushLoading && pushStatus ? (
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                  pushStatus.deviceSubscribed
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-line bg-surface text-muted'
                }`}
              >
                {pushStatus.deviceSubscribed ? tr('Ativo neste aparelho') : tr('Desativado neste aparelho')}
              </span>
            ) : null}
          </div>

          {!pushAvailable ? (
            <div className="mt-3">
              <Alert tone="info" title={tr("Navegador sem suporte")}>
                {tr("Este navegador não suporta notificações push. Use Chrome, Edge, Firefox ou Safari atualizado.")}
              </Alert>
            </div>
          ) : (
            <>
              {isIos() && !isStandalone() ? (
                <div className="mt-3">
                  <Alert tone="info" title={tr("No iPhone")}>
                    {tr("Adicione o AgendaPro à Tela de Início (Compartilhar › Adicionar à Tela de Início) e ative as notificações pelo app instalado. É preciso iOS 16.4 ou superior.")}
                  </Alert>
                </div>
              ) : null}

              {pushStatus?.enabled === false ? (
                <div className="mt-3">
                  <Alert tone="warning" title={tr("Ainda não configurado")}>
                    {tr("O servidor ainda não tem as chaves de notificação. Avise a administração.")}
                  </Alert>
                </div>
              ) : null}

              {pushError ? (
                <div className="mt-3"><Alert tone="warning" title={tr("Notificações")}>{pushError}</Alert></div>
              ) : null}

              {pushLoading ? (
                <div className="mt-3"><Spinner label={tr("Verificando notificações…")} /></div>
              ) : (
                <>
                  <p className="mt-3 text-sm text-muted">
                    {pushStatus && pushStatus.devices > 0
                      ? tr('{count} aparelho(s) da empresa recebendo avisos.', { count: pushStatus.devices })
                      : tr('Nenhum aparelho da empresa recebendo avisos ainda.')}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pushStatus?.deviceSubscribed ? (
                      <Button variant="secondary" loading={pushBusy === 'disable'} onClick={() => void onDisablePush()}>
                        {tr("Desativar neste aparelho")}
                      </Button>
                    ) : (
                      <Button loading={pushBusy === 'enable'} disabled={pushStatus?.enabled === false} onClick={() => void onEnablePush()}>
                        {tr("Ativar neste aparelho")}
                      </Button>
                    )}
                    {pushStatus?.deviceSubscribed ? (
                      <Button variant="ghost" loading={pushBusy === 'test'} onClick={() => void onTestPush()}>{tr("Enviar teste")}</Button>
                    ) : null}
                  </div>
                  <p className="nl-hint mt-3 block">
                    {tr("Funciona com o navegador aberto ou fechado. No Android use o Chrome e permita notificações; no computador, permita quando o navegador perguntar. Cada aparelho precisa ser ativado uma vez.")}
                  </p>
                </>
              )}
            </>
          )}
        </Card>
      ) : null}

      <Card as="section">
        <h2 className="mb-3 text-lg font-semibold">{tr("Sua conta")}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="nl-label">{tr("Nome completo")}</span>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={120} />
          </label>
          <label className="block">
            <span className="nl-label">{tr("Telefone")}</span>
            <Input value={userPhone} onChange={(e) => setUserPhone(e.target.value)} placeholder="5511999998888" />
          </label>
          <div className="sm:col-span-2">
            <Button variant="secondary" onClick={() => void saveUser()} loading={savingUser}>{tr("Salvar conta")}</Button>
          </div>
        </div>
        <p className="nl-hint mt-2">{tr("E-mail de acesso:")} <strong>{user?.email ?? '—'}</strong>{tr(". A troca de e-mail não é permitida por auto-serviço.")}</p>
      </Card>

      <Card as="section">
        <h2 className="mb-1 text-lg font-semibold">{tr("Alterar senha")}</h2>
        <p className="mb-3 text-sm text-muted">{tr("Ao alterar, todas as outras sessões são encerradas.")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="nl-label">{tr("Senha atual")}</span>
            <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} autoComplete="current-password" />
          </label>
          <label className="block">
            <span className="nl-label">{tr("Nova senha")}</span>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" minLength={8} />
            <span className="nl-hint mt-1 block">{tr("Mínimo de 8 caracteres.")}</span>
          </label>
        </div>
        <div className="mt-3">
          <Button onClick={() => void changePassword()} loading={savingPw} disabled={!curPw || newPw.length < 8}>{tr("Alterar senha")}</Button>
        </div>
      </Card>

      <Card as="section">
        <h2 className="mb-1 text-lg font-semibold text-danger">{tr("Excluir conta")}</h2>
        <p className="mb-3 text-sm text-muted">
          {tr("Desativa permanentemente o acesso. Por obrigação legal, registros de agendamentos e cobranças são preservados de forma protegida pelo prazo de retenção, mas você perde o acesso ao painel.")}
        </p>
        {deleteInfo ? <div className="mb-3"><Alert tone="warning" title={tr("Resultado")}>{deleteInfo}</Alert></div> : null}
        {!deleteInfo ? (
          <Button variant="danger" onClick={() => { setDeleteOpen(true); setConfirmText(''); setDeletePw(''); }}>{tr("Excluir minha conta")}</Button>
        ) : null}
      </Card>

      {deleteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={() => setDeleteOpen(false)}>
          <div role="dialog" aria-modal="true" aria-label={tr("Confirmar exclusão")} className="w-full max-w-md rounded-3xl bg-card p-5 shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
            <h2 className="mb-2 text-xl font-semibold">{tr("Confirmar exclusão")}</h2>
            <p className="mb-3 text-sm text-muted">{tr("Digite")} <strong>{tr("EXCLUIR")}</strong> {tr("e informe sua senha para confirmar.")}</p>
            <div className="space-y-3">
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={tr("EXCLUIR")} />
              <Input type="password" value={deletePw} onChange={(e) => setDeletePw(e.target.value)} placeholder={tr("Sua senha")} autoComplete="current-password" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setDeleteOpen(false)}>{tr("Cancelar")}</Button>
                <Button variant="danger" loading={deleting} disabled={confirmText !== 'EXCLUIR' || !deletePw} onClick={() => void deleteAccount()}>{tr("Excluir definitivamente")}</Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ErrorAlert({ error }: { error: Fail }): JSX.Element {
  const tone: 'warning' | 'danger' = error.status === 402 || error.status === 403 ? 'warning' : 'danger';
  const title = error.status === 402 ? 'Assinatura necessária' : error.status === 403 ? 'Permissão insuficiente' : 'Erro';
  return (
    <Alert tone={tone} title={title}>
      <p>{error.message}</p>
    </Alert>
  );
}
